import React, { useState, useMemo, useEffect } from 'react';
import {
  Download,
  FileText,
  FileSpreadsheet,
  Loader2,
  Users,
  DollarSign,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Building2,
  Wrench,
} from 'lucide-react';
import toast from '../../../utils/toast';
import api from '../../../services/api';
import { useDepartments, useTradeCategories } from '../../../hooks/useConfigData';
import DatePickerField from '../../../components/UI/DatePickerField';
import MultiPillSelect from '../../../components/UI/MultiPillSelect';
import DebouncedSearchInput from '../../../components/UI/DebouncedSearchInput';
import { fmtCurrency, fmtDays, fmtDate } from '../../../utils/pdfReportTemplate';
import {
  exportUnutilisedLeavePDF,
  exportUnutilisedLeaveExcel,
} from '../../../utils/unutilisedLeaveExport';
import { APP_SHELL_STICKY_TOP_CLASS } from '../../../constants/layout';

function isTerminatedInCutoffMonth(endDateStr, cutoffDateStr) {
  if (!endDateStr || !cutoffDateStr) return false;
  return endDateStr.slice(0, 7) === cutoffDateStr.slice(0, 7);
}

function fmtEntitlementDays(value) {
  if (value == null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

function getLastDayOfPreviousMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 0);
}

function toISODateString(d) {
  if (!d) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const CARD_COLOR_MAP = {
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
};

const SummaryCard = ({ label, value, sub, icon: Icon, color }) => {
  const c = CARD_COLOR_MAP[color] || CARD_COLOR_MAP.red;
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${c.bg}`}>
          <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${c.text}`} />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
          {sub && <p className="text-xs text-gray-500 mt-1 truncate">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

const ClassificationSection = ({
  title,
  rows,
  expanded,
  onToggle,
  cutoffDate,
  searchTerm,
  onSearchChange,
}) => {
  const normalizedSearch = (searchTerm || '').trim().toLowerCase();

  const filteredRows = useMemo(() => {
    if (!normalizedSearch) return rows;
    return rows.filter((r) => {
      const haystack = [
        r.staff_id,
        r.employee_name,
        r.department,
        r.trade_category,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [rows, normalizedSearch]);

  if (rows.length === 0) return null;

  const subtotalUnutilised = filteredRows.reduce(
    (s, r) => s + parseFloat(r.unutilised_balance || 0), 0,
  );
  const subtotalLiability = filteredRows.reduce(
    (s, r) => s + (r.monetary_liability ? parseFloat(r.monetary_liability) : 0), 0,
  );

  const countLabel = normalizedSearch
    ? `${filteredRows.length} of ${rows.length}`
    : `${rows.length} employee${rows.length !== 1 ? 's' : ''}`;

  return (
    <div className="mb-2">
      {/* Inline section header: chevron+title button | search | stats */}
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 hover:bg-gray-100/80 rounded-lg transition-colors">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 font-semibold text-gray-800 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span>{title}</span>
        </button>
        <DebouncedSearchInput
          value={searchTerm}
          onSearch={onSearchChange}
          placeholder={`Search ${title.toLowerCase()} by ID, name or department...`}
          theme="admin"
          className="flex-1 max-w-md"
        />
        <span className="ml-auto text-sm font-normal text-gray-500 whitespace-nowrap">
          {countLabel} |{' '}
          Unutilised: {fmtDays(subtotalUnutilised)} days |{' '}
          Liability: {fmtCurrency(subtotalLiability)}
        </span>
      </div>

      {expanded && (
        <>
        <div className="md:hidden space-y-3 p-4 mt-1">
          {filteredRows.length === 0 && normalizedSearch && (
            <div className="text-center text-sm text-gray-500 py-6">
              No employees match &ldquo;{searchTerm}&rdquo;
            </div>
          )}
          {filteredRows.map((r) => {
            const recentlyTerminated = isTerminatedInCutoffMonth(r.employment_end_date, cutoffDate);
            const balance = parseFloat(r.unutilised_balance || 0);
            const isNegativeBalance = balance < 0;
            const missingSalary = !r.basic_salary;
            let cardBg = 'bg-white';
            if (isNegativeBalance) cardBg = 'bg-red-50';
            else if (recentlyTerminated) cardBg = 'bg-amber-50';

            return (
              <div key={r.staff_id} className={`border border-gray-200 rounded-xl p-4 ${cardBg}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{r.employee_name}</p>
                    <p className="text-xs text-gray-500 font-mono">{r.staff_id} · {r.department}</p>
                  </div>
                  <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${isNegativeBalance ? 'text-red-600' : 'text-gray-900'}`}>
                    {fmtDays(r.unutilised_balance)} bal
                  </span>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500">Entitlement / Taken (YTD)</span>
                    <span className="text-gray-900 text-right tabular-nums">
                      {fmtEntitlementDays(r.al_entitlement)} / {fmtDays(r.al_consumed_ytd ?? r.al_consumed)}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500">Schedule</span>
                    <span className="text-gray-900 text-right">{r.work_schedule}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500">Basic Salary</span>
                    <span className="text-gray-900 text-right tabular-nums">
                      {r.basic_salary
                        ? fmtCurrency(r.basic_salary)
                        : <span className="text-red-400 text-xs italic">No data</span>}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-gray-500">Liability</span>
                    <span className="text-gray-900 text-right font-semibold tabular-nums">
                      {r.monetary_liability
                        ? fmtCurrency(r.monetary_liability)
                        : missingSalary
                          ? <span className="text-red-400 text-xs italic">No data</span>
                          : '-'}
                    </span>
                  </div>
                  {recentlyTerminated && (
                    <div className="flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Terminated this month
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {filteredRows.length > 0 && (
            <div className="bg-gray-100 border border-gray-200 rounded-xl p-4 text-sm">
              <div className="flex items-center justify-between font-semibold">
                <span>Sub-total ({title})</span>
                <span className="tabular-nums">{fmtDays(subtotalUnutilised)} · {fmtCurrency(subtotalLiability)}</span>
              </div>
            </div>
          )}
        </div>
        <div className="hidden md:block mt-1 overflow-x-auto md:overflow-visible">
          <table className="w-full min-w-[1100px] md:min-w-0 text-sm">
            <thead className={`sticky ${APP_SHELL_STICKY_TOP_CLASS} z-10`}>
              <tr className="bg-red-600 text-white text-xs shadow-sm">
                {/* Zone 1 — Identity */}
                <th className="px-3 py-2 text-left font-medium">Staff ID</th>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Dept</th>
                {/* Zone 2 — Employment */}
                <th className="px-3 py-2 text-center font-medium">Start Date</th>
                <th className="px-3 py-2 text-center font-medium">End Date</th>
                <th className="px-3 py-2 text-left font-medium">Service</th>
                {/* Zone 3 — Leave narrative */}
                <th className="px-3 py-2 text-right font-medium">Entitlement</th>
                <th className="px-3 py-2 text-right font-medium">C/F</th>
                <th className="px-3 py-2 text-right font-medium">Accrued</th>
                <th className="px-3 py-2 text-right font-medium">Taken (YTD)</th>
                <th className="px-3 py-2 text-right font-medium">Taken (Month)</th>
                <th className="px-3 py-2 text-right font-medium">Balance</th>
                {/* Zone 4 — Financial impact */}
                <th className="px-3 py-2 text-center font-medium">Schedule</th>
                <th className="px-3 py-2 text-right font-medium">Basic Salary</th>
                <th className="px-3 py-2 text-right font-medium">Gross Daily Rate</th>
                <th className="px-3 py-2 text-right font-medium">Liability</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.length === 0 && normalizedSearch && (
                <tr>
                  <td colSpan={16} className="px-3 py-6 text-center text-sm text-gray-500">
                    No employees match &ldquo;{searchTerm}&rdquo;
                  </td>
                </tr>
              )}
              {filteredRows.map((r) => {
                const recentlyTerminated = isTerminatedInCutoffMonth(r.employment_end_date, cutoffDate);
                const balance = parseFloat(r.unutilised_balance || 0);
                const isNegativeBalance = balance < 0;
                const missingSalary = !r.basic_salary;

                let rowBg = '';
                if (isNegativeBalance) rowBg = 'bg-red-50';
                else if (recentlyTerminated) rowBg = 'bg-amber-50';

                return (
                  <tr key={r.staff_id} className={`table-row-dense ${rowBg}`}>
                    {/* Zone 1 — Identity */}
                    <td className="px-3 py-2 font-mono text-xs">{r.staff_id}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.employee_name}</td>
                    <td className="px-3 py-2">{r.department}</td>
                    {/* Zone 2 — Employment */}
                    <td className="px-3 py-2 text-center text-xs">{fmtDate(r.employment_start_date)}</td>
                    <td className="px-3 py-2 text-center text-xs">
                      <span className="inline-flex items-center gap-1">
                        {fmtDate(r.employment_end_date)}
                        {recentlyTerminated && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.service_length}</td>
                    {/* Zone 3 — Leave narrative */}
                    <td className="px-3 py-2 text-right">{fmtEntitlementDays(r.al_entitlement)}</td>
                    <td className="px-3 py-2 text-right">{fmtDays(r.carry_forward)}</td>
                    <td className="px-3 py-2 text-right">{fmtDays(r.accrued_ytd)}</td>
                    <td className="px-3 py-2 text-right">{fmtDays(r.al_consumed_ytd ?? r.al_consumed)}</td>
                    <td className="px-3 py-2 text-right">{fmtDays(r.al_consumed_month)}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${isNegativeBalance ? 'text-red-600' : ''}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {fmtDays(r.unutilised_balance)}
                        {isNegativeBalance && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                      </span>
                    </td>
                    {/* Zone 4 — Financial impact */}
                    <td className="px-3 py-2 text-center">{r.work_schedule}</td>
                    <td className="px-3 py-2 text-right">
                      {r.basic_salary
                        ? fmtCurrency(r.basic_salary)
                        : <span className="text-red-400 text-xs italic">No data</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.daily_rate
                        ? fmtCurrency(r.daily_rate)
                        : <span className="text-red-400 text-xs italic">No data</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {r.monetary_liability
                        ? fmtCurrency(r.monetary_liability)
                        : missingSalary
                          ? <span className="text-red-400 text-xs italic">No data</span>
                          : '-'
                      }
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-100 font-semibold text-xs">
                <td colSpan={11} className="px-3 py-2 text-right">Sub-total ({title})</td>
                <td className="px-3 py-2 text-right">{fmtDays(subtotalUnutilised)}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2" />
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right">{fmtCurrency(subtotalLiability)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

const TEAMS_DEPT_NAME = 'Teams';

const CLASSIFICATION_OPTIONS = [
  { value: 'staff', label: 'Staff' },
  { value: 'worker', label: 'Worker' },
];

const UnutilisedLeaveReport = () => {
  const [cutoffDate, setCutoffDate] = useState(() => getLastDayOfPreviousMonth());
  const [departmentIds, setDepartmentIds] = useState([]);
  const [tradeCategoryIds, setTradeCategoryIds] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ staff: true, worker: true });
  const [searchTerms, setSearchTerms] = useState({ staff: '', worker: '' });
  const [exporting, setExporting] = useState(null);

  const { data: departments = [] } = useDepartments();
  const { data: tradeCategories = [] } = useTradeCategories();

  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: String(d.id), label: d.department_name })),
    [departments],
  );

  const tradeCategoryOptions = useMemo(
    () => tradeCategories.map((t) => ({ value: String(t.id), label: t.trade_code || t.name })),
    [tradeCategories],
  );

  const showTradeFilter = useMemo(() => {
    if (departmentIds.length === 0) return false;
    return departments.some(
      (d) =>
        departmentIds.includes(String(d.id)) &&
        d.department_name.toLowerCase() === TEAMS_DEPT_NAME.toLowerCase(),
    );
  }, [departmentIds, departments]);

  useEffect(() => {
    if (!showTradeFilter) setTradeCategoryIds([]);
  }, [showTradeFilter]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const cutoffStr = toISODateString(cutoffDate);
      const payload = { cutoff_date: cutoffStr };
      if (departmentIds.length > 0) payload.department_ids = departmentIds.map(Number);
      if (showTradeFilter && tradeCategoryIds.length > 0) {
        payload.trade_category_ids = tradeCategoryIds.map(Number);
      }
      if (classifications.length > 0) payload.classifications = classifications;

      const res = await api.post('/reporting/unutilised-leave-balance/', payload);
      setReportData(res.data);

      const total = res.data.total_employees || 0;
      toast.success(`Report generated — ${total} employee${total !== 1 ? 's' : ''}`);

      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} employee(s) had calculation errors`, { icon: '⚠️' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to generate report';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSectionSearch = (key, value) => {
    setSearchTerms((prev) => ({ ...prev, [key]: value }));
    if (value && !expandedSections[key]) {
      setExpandedSections((prev) => ({ ...prev, [key]: true }));
    }
  };

  const summary = reportData?.summary;
  const staffRows = reportData?.employees?.staff || [];
  const workerRows = reportData?.employees?.worker || [];
  const allRows = useMemo(() => [...staffRows, ...workerRows], [staffRows, workerRows]);

  const grandUnutilised = summary ? parseFloat(summary.grand.total_unutilised_days) : 0;
  const grandLiability = summary ? parseFloat(summary.grand.total_monetary_liability) : 0;
  const grandCount = summary ? summary.grand.count : 0;

  const handleExport = async (type) => {
    if (!reportData) return;
    setExporting(type);
    try {
      if (type === 'pdf') await exportUnutilisedLeavePDF(reportData);
      else if (type === 'excel') await exportUnutilisedLeaveExcel(reportData);
    } catch (err) {
      console.error(`Export ${type} failed:`, err);
      toast.error(`Failed to export ${type.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Unutilised Annual Leave Balance</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Leave liability report with monetary valuation</p>
        </div>
        {reportData && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport('pdf')}
              disabled={!!exporting}
              className="icon-button-admin disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to PDF"
              aria-label="Export to PDF"
            >
              {exporting === 'pdf'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <FileText className="w-4 h-4" />}
            </button>
            <button
              onClick={() => handleExport('excel')}
              disabled={!!exporting}
              className="icon-button-admin disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to Excel"
              aria-label="Export to Excel"
            >
              {exporting === 'excel'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <FileSpreadsheet className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>

      {/* Parameters bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Cutoff Date</label>
          <DatePickerField
            theme="admin"
            selected={cutoffDate}
            onChange={(date) => setCutoffDate(date)}
            maxDate={new Date()}
            placeholderText="Select cutoff date"
            wrapperClassName="w-48"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Department</label>
          <MultiPillSelect
            options={departmentOptions}
            value={departmentIds}
            onChange={setDepartmentIds}
            allLabel="All Departments"
            icon={Building2}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>
        {showTradeFilter && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Trade Category</label>
            <MultiPillSelect
              options={tradeCategoryOptions}
              value={tradeCategoryIds}
              onChange={setTradeCategoryIds}
              allLabel="All Trades"
              icon={Wrench}
              iconColor="text-red-600"
              theme="admin"
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Classification</label>
          <MultiPillSelect
            options={CLASSIFICATION_OPTIONS}
            value={classifications}
            onChange={setClassifications}
            allLabel="All Classifications"
            icon={Users}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>
        <button
          onClick={handleGenerate}
          disabled={loading || !cutoffDate}
          className="btn btn-admin-primary"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Generate Report
        </button>
      </div>

      {/* Results */}
      {reportData && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            <SummaryCard
              label="Total Employees"
              value={grandCount}
              sub={`Staff: ${summary.staff.count} | Worker: ${summary.worker.count}`}
              icon={Users}
              color="red"
            />
            <SummaryCard
              label="Total Unutilised Days"
              value={fmtDays(grandUnutilised)}
              sub={`Staff: ${fmtDays(summary.staff.total_unutilised_days)} | Worker: ${fmtDays(summary.worker.total_unutilised_days)}`}
              icon={TrendingDown}
              color="amber"
            />
            <SummaryCard
              label="Total Monetary Liability"
              value={fmtCurrency(grandLiability)}
              sub={`Staff: ${fmtCurrency(summary.staff.total_monetary_liability)} | Worker: ${fmtCurrency(summary.worker.total_monetary_liability)}`}
              icon={DollarSign}
              color="purple"
            />
          </div>

          {/* Data table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-2">
            <ClassificationSection
              title="Staff"
              rows={staffRows}
              expanded={expandedSections.staff}
              onToggle={() => toggleSection('staff')}
              cutoffDate={reportData?.cutoff_date}
              searchTerm={searchTerms.staff}
              onSearchChange={(v) => handleSectionSearch('staff', v)}
            />
            <ClassificationSection
              title="Worker"
              rows={workerRows}
              expanded={expandedSections.worker}
              onToggle={() => toggleSection('worker')}
              cutoffDate={reportData?.cutoff_date}
              searchTerm={searchTerms.worker}
              onSearchChange={(v) => handleSectionSearch('worker', v)}
            />

            {/* Grand total */}
            {allRows.length > 0 && (
              <>
                <div className="mt-2 px-4 py-3 bg-gray-800 text-white rounded-lg flex items-center justify-between text-sm font-semibold">
                  <span>Grand Total — {grandCount} employees</span>
                  <span className="flex items-center gap-6">
                    <span>Unutilised: {fmtDays(grandUnutilised)} days</span>
                    <span>Liability: {fmtCurrency(grandLiability)}</span>
                  </span>
                </div>
                <div className="mt-3 flex flex-col gap-1.5 text-xs text-gray-500 px-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-3 rounded bg-amber-50 border border-amber-200 shrink-0" />
                    <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                    <span>Terminated within cutoff month — verify final leave settlement &amp; encashment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-3 rounded bg-red-50 border border-red-200 shrink-0" />
                    <AlertCircle className="w-3 h-3 text-red-500 shrink-0" />
                    <span>Negative balance — employee consumed more leave than accrued (deduct from final pay)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 italic shrink-0">No data</span>
                    <span>— Salary record missing; liability cannot be computed</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Empty state */}
      {!reportData && !loading && (
        <div className="text-center py-16 text-gray-500">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Report Generated</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Select a cutoff date and click "Generate Report" to preview the unutilised
            annual leave balance and monetary liability for all employees.
          </p>
        </div>
      )}
    </div>
  );
};

export default UnutilisedLeaveReport;
